import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import type { AboutUs, Partner, TeamMember, Work } from '../types/api';
import { Eyebrow, PageHero, Reveal, SafeImage, Section, Stats } from '../components/ui';
import { AlternatingBlocks, CommitmentColumns, LogoGrid, SectorPhotoGrid, VisionBlock, type Block } from '../components/about';
import { TeamSpotlight } from '../components/home';
import { useNavData } from '../components/layout/useNavData';
import { paragraphs, splitLead } from '../utils/text';

type Data = { about: AboutUs | null; works: Work[]; team: TeamMember[]; partners: Partner[] };

/** « plus de 10 ans d'expérience » dans le texte de présentation → « 10+ ». */
const yearsFrom = (text = ''): string | null => {
  const m = text.match(/(?:plus de|depuis)\s+(\d{1,2})\s+ans/i);
  return m ? `${m[1]}+` : null;
};

export default function About() {
  const [data, setData] = useState<Data>({ about: null, works: [], team: [], partners: [] });
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading');
  const { sectors, services } = useNavData();

  useEffect(() => {
    let alive = true;
    Promise.allSettled([apiService.getAboutUs(), apiService.getWorks({ enable: true }), apiService.getTeamMembers(), apiService.getPartners()])
      .then(([a, w, t, p]) => {
        if (!alive) return;
        if (a.status !== 'fulfilled') { setStatus('error'); return; }
        setData({ about: a.value, works: w.status === 'fulfilled' ? w.value : [], team: t.status === 'fulfilled' ? t.value : [], partners: p.status === 'fulfilled' ? p.value : [] });
        setStatus('ok');
      });
    return () => { alive = false; };
  }, []);

  const { about, works, team, partners } = data;
  const intro = useMemo(() => splitLead(about?.description, 230), [about?.description]);
  const purpose = useMemo(() => splitLead(about?.our_purpose, 300), [about?.our_purpose]);
  const commitment = useMemo(() => splitLead(about?.our_commitment, 200), [about?.our_commitment]);
  // Vision: le paragraphe de la présentation qui parle d'ambition (sinon la suite de l'objectif)
  const vision = useMemo(() => paragraphs(about?.description).find((p) => /ambitionne|ambition/i.test(p)) ?? purpose.rest[0] ?? '', [about?.description, purpose.rest]);
  const visionImage = works.find((w) => w.cover_image?.url)?.cover_image?.url;
  const method = about?.how_we_show_up?.[0];
  const methodParas = paragraphs(method?.description);
  // Les « principes » sont écrits en lignes numérotées (« 1. … »): on les sépare du texte courant pour en faire une liste
  const steps = methodParas.filter((p) => /^\d{1,2}\.\s/.test(p)).map((p) => p.replace(/^\d{1,2}\.\s*/, ''));
  const prose = methodParas.slice(1).filter((p) => !/^\d{1,2}\.\s/.test(p));

  // Les chiffres viennent des listes réelles (le point d'accès « statistiques » compte aussi des éléments désactivés)
  const stats = [
    works.length ? { value: String(works.length), label: 'Réalisations' } : null,
    services.length ? { value: String(services.length), label: 'Services' } : null,
    sectors.length ? { value: String(sectors.length), label: "Secteurs d'intervention" } : null,
    yearsFrom(about?.description) ? { value: yearsFrom(about?.description)!, label: "Années d'expérience" } : null,
    team.length ? { value: String(team.length), label: "Membres de l'équipe" } : null,
    partners.length ? { value: String(partners.length), label: 'Partenaires' } : null,
  ].filter((s): s is { value: string; label: string } => !!s);

  const blocks: Block[] = (about?.commitment ?? []).flatMap((c) => (c.details ?? []).filter((d) => d.enable !== false).map((d) => ({ id: d.id, title: d.title, text: d.description, image: d.image?.url })));

  if (status === 'loading') {
    return <div aria-busy className="animate-pulse"><div className="h-[32rem] bg-secondary-300 md:h-[38rem]" /><div className="site-container space-y-4 py-16"><div className="h-10 w-3/4 bg-secondary-200" /><div className="h-4 w-full bg-secondary-200" /></div></div>;
  }
  if (status === 'error' || !about) {
    return <Section eyebrow="À propos" heading="Page indisponible" background="white"><p className="max-w-xl text-lg text-ink">Impossible de charger cette page pour le moment. Réessayez dans un instant.</p></Section>;
  }

  return (
    <div id="content" className="text-black">
      {/* 1. Hero */}
      <PageHero eyebrow="À propos de Flex Group" title="Bâtir un avenir meilleur, ensemble" image={about.image?.url} />

      {/* 2. Présentation: phrase d'accroche en grand, puis le texte */}
      <Section pattern="top-right" background="white">
        <div className="max-w-5xl">
          <Reveal><h2 className="font-sans text-[1.5rem] font-bold normal-case leading-snug tracking-normal !text-brand-darker md:text-[2rem]">{intro.lead}</h2></Reveal>
          <div className="mt-10 space-y-5 text-[1.0625rem] leading-relaxed">
            {intro.rest.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
        </div>
      </Section>

      {/* 3. Engagements (icône, titre, 3 lignes) */}
      <CommitmentColumns heading="Chaque décision a un impact" intro={commitment.lead} items={about.commitment ?? []} />

      {/* 4. Chiffres */}
      {stats.length > 0 && (
        <section className="bg-charcoal py-16 text-white md:py-24">
          <div className="site-container">
            <Eyebrow tone="light" className="mb-12">Flex Group en chiffres</Eyebrow>
            <Stats layout="grid" tone="dark" stats={stats} />
          </div>
        </section>
      )}

      {/* 5. Vision, avec grande photo */}
      {vision && <VisionBlock heading="Une vision tournée vers l'Afrique centrale" text={vision} image={visionImage} cta={{ label: 'Découvrir nos réalisations', to: '/portfolio' }} />}

      {/* 6. Objectif */}
      <section className="relative overflow-hidden bg-charcoal py-16 text-white md:py-24">
        <svg aria-hidden viewBox="0 0 180 240" className="pointer-events-none absolute right-0 top-0 hidden h-56 w-40 text-brand lg:block" fill="none" stroke="currentColor" strokeWidth="3"><path d="M180 0V120L90 30L0 120" /></svg>
        <div className="site-container relative">
          <Eyebrow tone="light" className="mb-6">Notre objectif</Eyebrow>
          <Reveal><p className="max-w-4xl font-sans text-[1.5rem] font-semibold leading-snug !text-white md:text-[2rem]">{purpose.lead}</p></Reveal>
        </div>
      </section>

      {/* 7. Secteurs en grille de photos */}
      <SectorPhotoGrid sectors={sectors} heading="Une expertise qui couvre tout le chantier" intro="Des études techniques à la livraison, nos équipes interviennent dans des secteurs variés du génie civil et de la construction." />

      {/* 8. Méthode de travail: texte et photo à gauche (collés), frise des principes à droite */}
      {method && (
        <section className="bg-white py-16 text-black md:py-28">
          <div className="site-container grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <Eyebrow className="mb-4">Comment nous intervenons</Eyebrow>
              <Reveal>
                <h2 className="font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[2.75rem]">{method.title}</h2>
                <p className="mt-6 text-[1.0625rem] leading-relaxed">{methodParas[0]}</p>
              </Reveal>
              {method.image?.url && (
                <Reveal delay={0.08}><div className="mt-8 aspect-[4/3] overflow-hidden bg-secondary-200"><SafeImage src={method.image.url} alt={method.title} className="h-full w-full object-cover" /></div></Reveal>
              )}
            </div>

            <div>
              {prose.length > 0 && <div className="space-y-5 text-[1.0625rem] leading-relaxed">{prose.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}</div>}
              {steps.length > 0 && (
                <ol className={`relative ${prose.length > 0 ? 'mt-12' : ''}`}>
                  <span aria-hidden className="absolute bottom-3 left-[1.1rem] top-3 w-px bg-line" />
                  {steps.map((t, n) => (
                    <li key={t.slice(0, 24)} className="relative flex gap-6 pb-7 last:pb-0">
                      <span className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-brand bg-white font-display text-[0.9375rem] font-bold text-brand-darker">{n + 1}</span>
                      <p className="pt-1 text-[1.0625rem] leading-relaxed">{t}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 9. Détails des engagements: photo + texte en alternance */}
      <div id="engagements-detail"><AlternatingBlocks eyebrow="Comment nous nous engageons" heading="Des engagements tenus sur chaque chantier" intro="Qualité, durabilité, innovation: ce que nous faisons concrètement pour les respecter." blocks={blocks} /></div>

      {/* 10. Équipe */}
      {team.length > 0 && <TeamSpotlight members={team} intro={{ title: 'Les personnes qui bâtissent avec vous', text: 'Ingénieurs, techniciens et chefs de chantier: une équipe engagée à chaque étape de votre projet.' }} />}

      {/* 11. Partenaires */}
      <LogoGrid eyebrow="Nos partenaires" partners={partners} />
    </div>
  );
}
